import './DisplayNameDialog.scss';

import { Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { DisplayNameDialogProps } from 'types/Dialog.types';

/**
 * Dialog for overriding a deckline's display name.
 * When display_name is null, the kpi_name is shown as placeholder text.
 * Submitting an empty value clears the override (sets display_name to null).
 */
const DisplayNameDialog = ({
  isDialogOpen,
  setIsDialogOpen,
  currentDisplayName,
  kpiName,
  handleConfirm,
}: DisplayNameDialogProps) => {
  const [inputValue, setInputValue] = useState(currentDisplayName ?? '');

  /** Sync inputValue with currentDisplayName each time the dialog opens, since the component stays mounted. */
  useEffect(() => {
    if (isDialogOpen) {
      setInputValue(currentDisplayName ?? '');
    }
  }, [isDialogOpen, currentDisplayName]);

  const handleClose = () => {
    setIsDialogOpen?.(false);
  };

  const handleSubmit = () => {
    const trimmed = inputValue.trim();
    handleConfirm(trimmed.length > 0 ? trimmed : null);
    handleClose();
  };

  return (
    <Dialog
      className='display-name-dialog'
      open={isDialogOpen ?? false}
      onClose={handleClose}
      disableRestoreFocus // Allows the TextField to be focused on open
      aria-labelledby='display-name-dialog-title'
    >
      <DialogTitle id='display-name-dialog-title'>Override Display Name</DialogTitle>
      <DialogContent>
        <Stack spacing={1} direction='column'>
          <Typography className='dialog-description'>
            Adding a display name will change the Deckline Name for this row on this page only. Changes made in the KPI
            Library will not update this value.
          </Typography>
          <Divider />
          <Stack spacing={1} useFlexGap direction='row' className='display-name-dialog__input-row'>
            <TextField
              className='display-name-input'
              autoFocus
              fullWidth
              margin='dense'
              label='Deckline Name Override'
              placeholder={kpiName}
              autoComplete='off'
              autoCorrect='off'
              value={inputValue}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
            />
            <Button
              className='display-name-reset-button'
              variant='outlined'
              color='primary'
              onClick={() => setInputValue('')}
              disabled={!inputValue}
            >
              Remove Changes
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button className='dialog-button cancel-button' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' className='dialog-button confirm-button' onClick={handleSubmit}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisplayNameDialog;
